/**
 * Conversation Management Hooks
 *
 * Hooks for managing conversation configuration entities:
 * - Types
 * - Categories
 * - Subcategories
 * - Status
 * - States
 * - Impacts
 * - Urgencies
 * - Priorities
 * - Approval States
 * - Approval Levels
 * - Tags
 * - Additional Properties
 * - Root Cause Analysis
 */
import { useCallback, useState, useEffect } from 'react'
import { api } from '../api/client.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversationType {
  id: string
  name: string
  description: string | null
  status: 'active' | 'inactive'
  enableInClientPortal: boolean
  createdAt: string
  updatedAt: string
}

export interface ConversationTypeDropdown {
  id: string
  name: string
}

export interface ConversationCategory {
  id: string
  name: string
  description: string | null
  // Optional — a category need not be scoped to a conversation type. Null
  // when unset, in which case typeName is null too (the list query LEFT JOINs
  // conversation_types).
  typeId: string | null
  typeName: string | null
  status: 'active' | 'inactive'
  enableInClientPortal: boolean
  createdAt: string
  updatedAt: string
}

export interface ConversationCategoryDropdown {
  id: string
  name: string
}

export interface ConversationSubcategory {
  id: string
  name: string
  description: string | null
  categoryId: string
  status: 'active' | 'inactive'
  enableInClientPortal: boolean
  createdAt: string
  updatedAt: string
}

export interface ConversationStatus {
  id: string
  name: string
  description: string | null
  status: boolean
  forClient: boolean
  enableInClientPortal: boolean
  enableClientToReply: boolean
  allowClientToEdit: boolean
  statusColor: string
  statusColorType: 'solid' | 'gradient'
  slaTimer: boolean
  /** The status every newly created conversation opens on. Only one status per
   *  tenant carries it; with none set, conversations open on "Open". */
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface ConversationState {
  id: string
  name: string
  description: string | null
  status: 'active' | 'inactive'
  enableInClientPortal: boolean
  mandatoryForChanges: boolean
  statusColor: string
  createdAt: string
  updatedAt: string
}

export interface ConversationImpact {
  id: string
  name: string
  description: string | null
  status: boolean
  enableInClientPortal: boolean
  statusColor: string
  createdAt: string
  updatedAt: string
}

export interface ConversationUrgency {
  id: string
  name: string
  description: string | null
  status: boolean
  enableInClientPortal: boolean
  statusColor: string
  createdAt: string
  updatedAt: string
}

export interface ConversationPriority {
  id: string
  name: string
  description: string | null
  status: boolean
  enableInClientPortal: boolean
  statusColor: string
  createdAt: string
  updatedAt: string
}

export interface ConversationComplexity {
  id: string
  name: string
  description: string | null
  status: boolean
  enableInClientPortal: boolean
  statusColor: string
  createdAt: string
  updatedAt: string
}

export interface ConversationChecklistItem {
  id: string
  checklistId: string
  slNo: number
  itemName: string
  description: string | null
  createdAt: string
  updatedAt: string
}

export interface ConversationChecklist {
  id: string
  name: string
  description: string | null
  status: boolean
  createdAt: string
  updatedAt: string
  items?: ConversationChecklistItem[]
}

export interface ConversationQuickReply {
  id: string
  name: string
  description: string | null
  status: boolean
  replyType: string
  categoryId: string
  subcategoryId: string
  categoryName: string | null
  subcategoryName: string | null
  reply: string
  createdAt: string
  updatedAt: string
}

export interface ConversationQuickReplyCategory {
  id: string
  name: string
  description: string | null
  status: boolean
  createdAt: string
  updatedAt: string
}

export interface ConversationQuickReplySubcategory {
  id: string
  name: string
  description: string | null
  status: boolean
  categoryId: string
  categoryName: string | null
  createdAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Specific Hooks
// ---------------------------------------------------------------------------

export function useConversationTypes() {
  const [items, setItems] = useState<ConversationType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ types: ConversationType[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/types?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.types ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching types')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationType> => {
      const created = await api.post<ConversationType>('/admin/conversation-management/types', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationType> => {
      const updated = await api.patch<ConversationType>(`/admin/conversation-management/types/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/types/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationType> => {
      return api.get<ConversationType>(`/admin/conversation-management/types/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationTypesDropdown() {
  const [types, setTypes] = useState<ConversationTypeDropdown[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    api.get<{ types: ConversationTypeDropdown[] }>(
      '/admin/conversation-management/types/dropdown'
    )
      .then((res) => {
        if (!cancelled) {
          setTypes(res.types ?? [])
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching types')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return { types, loading, error }
}

export function useConversationCategoriesDropdown() {
  const [categories, setCategories] = useState<ConversationCategoryDropdown[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    api.get<{ categories: ConversationCategoryDropdown[] }>(
      '/admin/conversation-management/categories/dropdown'
    )
      .then((res) => {
        if (!cancelled) {
          setCategories(res.categories ?? [])
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching categories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return { categories, loading, error }
}

export function useConversationCategories() {
  const [items, setItems] = useState<ConversationCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string | boolean>>({})
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (filters.typeId) params.append('typeId', filters.typeId as string)
    if (filters.status) params.append('status', filters.status as string)
    if (filters.enableInClientPortal !== undefined) {
      params.append('enableInClientPortal', String(filters.enableInClientPortal))
    }

    api.get<{ categories: ConversationCategory[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/categories?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.categories ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching categories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search, filters])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationCategory> => {
      const created = await api.post<ConversationCategory>('/admin/conversation-management/categories', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationCategory> => {
      const updated = await api.patch<ConversationCategory>(`/admin/conversation-management/categories/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/categories/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationCategory> => {
      return api.get<ConversationCategory>(`/admin/conversation-management/categories/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, filters, setFilters, total, totalPages }
}

export function useConversationSubcategories() {
  const [items, setItems] = useState<ConversationSubcategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ subcategories: ConversationSubcategory[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/subcategories?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.subcategories ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching subcategories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationSubcategory> => {
      const created = await api.post<ConversationSubcategory>('/admin/conversation-management/subcategories', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationSubcategory> => {
      const updated = await api.patch<ConversationSubcategory>(`/admin/conversation-management/subcategories/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/subcategories/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationSubcategory> => {
      return api.get<ConversationSubcategory>(`/admin/conversation-management/subcategories/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationStatus() {
  const [items, setItems] = useState<ConversationStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ statuses: ConversationStatus[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/status?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.statuses ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching statuses')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationStatus> => {
      const created = await api.post<ConversationStatus>('/admin/conversation-management/status', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationStatus> => {
      const updated = await api.patch<ConversationStatus>(`/admin/conversation-management/status/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/status/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationStatus> => {
      return api.get<ConversationStatus>(`/admin/conversation-management/status/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationStates() {
  const [items, setItems] = useState<ConversationState[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ states: ConversationState[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/states?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.states ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching states')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationState> => {
      const created = await api.post<ConversationState>('/admin/conversation-management/states', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationState> => {
      const updated = await api.patch<ConversationState>(`/admin/conversation-management/states/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/states/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationState> => {
      return api.get<ConversationState>(`/admin/conversation-management/states/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationImpacts() {
  const [items, setItems] = useState<ConversationImpact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ impacts: ConversationImpact[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/impacts?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.impacts ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching impacts')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationImpact> => {
      const created = await api.post<ConversationImpact>('/admin/conversation-management/impacts', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationImpact> => {
      const updated = await api.patch<ConversationImpact>(`/admin/conversation-management/impacts/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/impacts/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationImpact> => {
      return api.get<ConversationImpact>(`/admin/conversation-management/impacts/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationUrgencies() {
  const [items, setItems] = useState<ConversationUrgency[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ urgencies: ConversationUrgency[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/urgencies?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.urgencies ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching urgencies')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationUrgency> => {
      const created = await api.post<ConversationUrgency>('/admin/conversation-management/urgencies', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationUrgency> => {
      const updated = await api.patch<ConversationUrgency>(`/admin/conversation-management/urgencies/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/urgencies/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationUrgency> => {
      return api.get<ConversationUrgency>(`/admin/conversation-management/urgencies/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationPriorities() {
  const [items, setItems] = useState<ConversationPriority[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ priorities: ConversationPriority[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/priorities?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.priorities ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching priorities')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationPriority> => {
      const created = await api.post<ConversationPriority>('/admin/conversation-management/priorities', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationPriority> => {
      const updated = await api.patch<ConversationPriority>(`/admin/conversation-management/priorities/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/priorities/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationPriority> => {
      return api.get<ConversationPriority>(`/admin/conversation-management/priorities/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationComplexities() {
  const [items, setItems] = useState<ConversationComplexity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ complexities: ConversationComplexity[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/complexities?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.complexities ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching complexities')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationComplexity> => {
      const created = await api.post<ConversationComplexity>('/admin/conversation-management/complexities', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationComplexity> => {
      const updated = await api.patch<ConversationComplexity>(`/admin/conversation-management/complexities/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/complexities/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationComplexity> => {
      return api.get<ConversationComplexity>(`/admin/conversation-management/complexities/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationChecklist() {
  const [items, setItems] = useState<ConversationChecklist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())

    api.get<{ checklists: ConversationChecklist[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/checklists?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.checklists ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching checklists')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationChecklist> => {
      const res = await api.post<{ checklist: ConversationChecklist }>('/admin/conversation-management/checklists', body)
      const created = res.checklist
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationChecklist> => {
      const res = await api.patch<{ checklist: ConversationChecklist }>(`/admin/conversation-management/checklists/${id}`, body)
      const updated = res.checklist
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/checklists/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationChecklist> => {
      const res = await api.get<{ checklist: ConversationChecklist }>(`/admin/conversation-management/checklists/${id}`)
      return res.checklist
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationQuickReplies() {
  const [items, setItems] = useState<ConversationQuickReply[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ quickReplies: ConversationQuickReply[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/quick-replies?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.quickReplies ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching quick replies')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationQuickReply> => {
      const created = await api.post<ConversationQuickReply>('/admin/conversation-management/quick-replies', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationQuickReply> => {
      const updated = await api.patch<ConversationQuickReply>(`/admin/conversation-management/quick-replies/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/quick-replies/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationQuickReply> => {
      return api.get<ConversationQuickReply>(`/admin/conversation-management/quick-replies/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationQuickReplyCategories() {
  const [items, setItems] = useState<ConversationQuickReplyCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ categories: ConversationQuickReplyCategory[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/quick-reply-categories?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.categories ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching quick reply categories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationQuickReplyCategory> => {
      const created = await api.post<ConversationQuickReplyCategory>('/admin/conversation-management/quick-reply-categories', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationQuickReplyCategory> => {
      const updated = await api.patch<ConversationQuickReplyCategory>(`/admin/conversation-management/quick-reply-categories/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/quick-reply-categories/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationQuickReplyCategory> => {
      return api.get<ConversationQuickReplyCategory>(`/admin/conversation-management/quick-reply-categories/${id}`)
    },
    [],
  )

  const fetchDropdown = useCallback(async (): Promise<Array<{ id: string; name: string }>> => {
    const res = await api.get<{ categories: Array<{ id: string; name: string }> }>('/admin/conversation-management/quick-reply-categories/dropdown')
    return res.categories ?? []
  }, [])

  return { items, loading, error, refetch, create, update, remove, getById, fetchDropdown, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

export function useConversationQuickReplyCategoriesDropdown() {
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    
    api.get<{ categories: Array<{ id: string; name: string }> }>('/admin/conversation-management/quick-reply-categories/dropdown')
      .then((res) => {
        if (!cancelled) {
          setCategories(res.categories ?? [])
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching categories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return { categories, loading, error }
}

export function useConversationQuickReplySubcategoriesDropdown(categoryId?: string) {
  const [subcategories, setSubcategories] = useState<Array<{ id: string; name: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    
    const params = new URLSearchParams()
    if (categoryId) params.append('categoryId', categoryId)
    
    api.get<{ subcategories: Array<{ id: string; name: string }> }>(`/admin/conversation-management/quick-reply-subcategories/dropdown?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setSubcategories(res.subcategories ?? [])
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching subcategories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [categoryId])

  return { subcategories, loading, error }
}

export function useConversationQuickReplySubcategories() {
  const [items, setItems] = useState<ConversationQuickReplySubcategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ subcategories: ConversationQuickReplySubcategory[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/quick-reply-subcategories?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.subcategories ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching quick reply subcategories')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationQuickReplySubcategory> => {
      const created = await api.post<ConversationQuickReplySubcategory>('/admin/conversation-management/quick-reply-subcategories', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationQuickReplySubcategory> => {
      const updated = await api.patch<ConversationQuickReplySubcategory>(`/admin/conversation-management/quick-reply-subcategories/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/quick-reply-subcategories/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationQuickReplySubcategory> => {
      return api.get<ConversationQuickReplySubcategory>(`/admin/conversation-management/quick-reply-subcategories/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

// ---------------------------------------------------------------------------
// Approval States
// ---------------------------------------------------------------------------

export interface ConversationApprovalState {
  id: string
  name: string
  description: string | null
  status: boolean
  mandatoryForChanges: boolean
  colorCode: string | null
  createdAt: string
  updatedAt: string
}

export function useConversationApprovalStates() {
  const [items, setItems] = useState<ConversationApprovalState[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ items: ConversationApprovalState[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/conversation-management/approval-states?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.items ?? [])
          setTotal(res.total ?? 0)
          setTotalPages(res.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching approval states')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationApprovalState> => {
      const created = await api.post<ConversationApprovalState>('/admin/conversation-management/approval-states', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationApprovalState> => {
      const updated = await api.patch<ConversationApprovalState>(`/admin/conversation-management/approval-states/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/approval-states/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationApprovalState> => {
      return api.get<ConversationApprovalState>(`/admin/conversation-management/approval-states/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

// ---------------------------------------------------------------------------
// Approval Levels
// ---------------------------------------------------------------------------

export interface ConversationApprovalLevel {
  id: string
  name: string
  description: string | null
  status: boolean
  createdAt: string
  updatedAt: string
}

export function useConversationApprovalLevels() {
  const [items, setItems] = useState<ConversationApprovalLevel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ items: ConversationApprovalLevel[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/conversation-management/approval-levels?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.items ?? [])
          setTotal(res.total ?? 0)
          setTotalPages(res.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching approval levels')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationApprovalLevel> => {
      const created = await api.post<ConversationApprovalLevel>('/admin/conversation-management/approval-levels', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationApprovalLevel> => {
      const updated = await api.patch<ConversationApprovalLevel>(`/admin/conversation-management/approval-levels/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/approval-levels/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationApprovalLevel> => {
      return api.get<ConversationApprovalLevel>(`/admin/conversation-management/approval-levels/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export interface ConversationTag {
  id: string
  name: string
  description: string | null
  status: boolean
  createdAt: string
  updatedAt: string
}

export function useConversationTags() {
  const [items, setItems] = useState<ConversationTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    
    api.get<{ items: ConversationTag[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/conversation-management/tags?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.items ?? [])
          setTotal(res.total ?? 0)
          setTotalPages(res.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching tags')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationTag> => {
      const created = await api.post<ConversationTag>('/admin/conversation-management/tags', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationTag> => {
      const updated = await api.patch<ConversationTag>(`/admin/conversation-management/tags/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/tags/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationTag> => {
      return api.get<ConversationTag>(`/admin/conversation-management/tags/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

// ---------------------------------------------------------------------------
// Dynamic Additional Properties
// ---------------------------------------------------------------------------

export interface DynamicAdditionalPropertyMetadata {
  id: number
  columnName: string
  displayName: string
  dataType: 'text' | 'number' | 'decimal' | 'float' | 'varchar' | 'date' | 'datetime' | 'checkbox' | 'json' | 'uuid'
  inputType: 'textbox' | 'textarea' | 'number' | 'date' | 'datetime' | 'checkbox' | 'dropdown' | 'email' | 'phone' | 'url' | 'toggle' | 'attachment' | 'editor' | 'integer' | 'decimal' | 'rating' | 'json' | 'submit-button'
  isRequired: boolean
  isVisible: 'agent' | 'client' | 'both'
  isReadOnly: boolean
  defaultValue: string | null
  placeholder: string | null
  validationRegex: string | null
  minLength: number | null
  maxLength: number | null
  hasIndex: boolean
  referenceTable: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateAdditionalPropertyPayload {
  columnName: string
  displayName: string
  dataType: 'text' | 'number' | 'decimal' | 'float' | 'varchar' | 'date' | 'datetime' | 'checkbox' | 'json' | 'uuid'
  inputType: 'textbox' | 'textarea' | 'number' | 'date' | 'datetime' | 'checkbox' | 'dropdown' | 'email' | 'phone' | 'url' | 'toggle' | 'attachment' | 'editor' | 'integer' | 'decimal' | 'rating' | 'json' | 'submit-button'
  isRequired?: boolean
  isVisible?: 'agent' | 'client' | 'both'
  isReadOnly?: boolean
  defaultValue?: string | null
  placeholder?: string | null
  validationRegex?: string | null
  minLength?: number | null
  maxLength?: number | null
  createIndex?: boolean
  referenceTable?: string | null
}

export interface DropdownOption {
  id: number
  metadataId: number
  label: string
  value: string
  parentOption: string | null
  fieldPosition: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateDropdownOptionPayload {
  label: string
  value: string
  parentOption?: string | null
  fieldPosition?: number
  isActive?: boolean
}

export interface UpdateDropdownOptionPayload {
  label?: string
  value?: string
  parentOption?: string | null
  fieldPosition?: number
  isActive?: boolean
}

export interface PropertyDependency {
  id: number
  metadataId: number
  dependsOnPropertyId: number
  operator: string
  expectedValue: string | null
  action: string
  condition: string
  cssClass: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateDependencyPayload {
  dependsOnPropertyId: number
  operator: string
  expectedValue?: string | null
  action: string
  condition?: string
  cssClass?: string | null
}

export interface UpdateDependencyPayload {
  dependsOnPropertyId?: number
  operator?: string
  expectedValue?: string | null
  action?: string
  condition?: string
  cssClass?: string | null
}

export interface RenameAdditionalPropertyPayload {
  newColumnName?: string
  newDisplayName?: string
  isRequired?: boolean
  isVisible?: 'agent' | 'client' | 'both'
  isReadOnly?: boolean
  defaultValue?: string | null
  placeholder?: string | null
  validationRegex?: string | null
  minLength?: number | null
  maxLength?: number | null
  referenceTable?: string | null
}

export function useConversationAdditionalProperties(options: { limit?: number } = {}) {
  const queryLimit = options.limit ?? 10
  const [metadata, setMetadata] = useState<DynamicAdditionalPropertyMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [search, setSearch] = useState('')
  const [summary, setSummary] = useState<{ total: number; agent: number; client: number; both: number }>({ total: 0, agent: 0, client: 0, both: 0 })
  const [pagination, setPagination] = useState<{ total: number; totalPages: number; page: number; limit: number }>({ total: 0, totalPages: 1, page: 1, limit: queryLimit })
  const [currentPage, setCurrentPage] = useState(1)

  // Dropdown options management
  const [dropdownOptions, setDropdownOptions] = useState<Record<number, DropdownOption[]>>({})
  const [dropdownOptionsLoading, setDropdownOptionsLoading] = useState<Record<number, boolean>>({})

  // Dependencies management
  const [dependencies, setDependencies] = useState<Record<number, PropertyDependency[]>>({})
  const [dependenciesLoading, setDependenciesLoading] = useState<Record<number, boolean>>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const params = new URLSearchParams()
    params.append('page', currentPage.toString())
    params.append('limit', queryLimit.toString())

    api.get<{ metadata: DynamicAdditionalPropertyMetadata[]; total: number; totalPages: number; page: number; limit: number; summary: { total: number; agent: number; client: number; both: number } }>(
      `/admin/conversation-management/additional-properties/metadata?${params.toString()}`,
    )
      .then((res) => {
        if (!cancelled) {
          setMetadata(res.metadata ?? [])
          setSummary(res.summary ?? { total: 0, agent: 0, client: 0, both: 0 })
          setPagination({
            total: res.total,
            totalPages: res.totalPages,
            page: res.page,
            limit: res.limit,
          })
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching additional properties metadata')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, currentPage, queryLimit])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const createProperty = useCallback(
    async (payload: CreateAdditionalPropertyPayload): Promise<DynamicAdditionalPropertyMetadata> => {
      const res = await api.post<{ metadata: DynamicAdditionalPropertyMetadata }>(
        '/admin/conversation-management/additional-properties',
        payload,
      )
      setMetadata((prev) => [...prev, res.metadata])
      return res.metadata
    },
    [],
  )

  const renameProperty = useCallback(
    async (columnName: string, payload: RenameAdditionalPropertyPayload): Promise<void> => {
      await api.patch<{ success: boolean }>(
        `/admin/conversation-management/additional-properties/${columnName}`,
        payload,
      )
      await refetch()
    },
    [refetch],
  )

  const deleteProperty = useCallback(
    async (columnName: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/additional-properties/${columnName}`)
      setMetadata((prev) => prev.filter((m) => m.columnName !== columnName))
    },
    [],
  )

  // Dropdown options CRUD
  const fetchDropdownOptions = useCallback(async (metadataId: number): Promise<void> => {
    setDropdownOptionsLoading((prev) => ({ ...prev, [metadataId]: true }))
    try {
      const res = await api.get<{ options: DropdownOption[] }>(
        `/admin/conversation-management/additional-properties/${metadataId}/dropdown-options`
      )
      setDropdownOptions((prev) => ({ ...prev, [metadataId]: res.options ?? [] }))
    } catch (err) {
      console.error('Failed to fetch dropdown options:', err)
    } finally {
      setDropdownOptionsLoading((prev) => ({ ...prev, [metadataId]: false }))
    }
  }, [])

  /**
   * Fetch dropdown options filtered by a parent value (cascading).
   * Results are stored under a composite key `"{metadataId}_{parentValue}"` so the
   * full option list (stored under `metadataId`) is preserved for other consumers.
   */
  const fetchDropdownOptionsByParent = useCallback(async (metadataId: number, parentValue: string): Promise<void> => {
    const compositeKey = `${metadataId}_${parentValue}` as unknown as number
    setDropdownOptionsLoading((prev) => ({ ...prev, [compositeKey]: true }))
    try {
      const res = await api.get<{ options: DropdownOption[] }>(
        `/admin/conversation-management/additional-properties/${metadataId}/dropdown-options?parentOption=${encodeURIComponent(parentValue)}`
      )
      setDropdownOptions((prev) => ({ ...prev, [compositeKey]: res.options ?? [] }))
    } catch (err) {
      console.error('Failed to fetch dropdown options by parent:', err)
    } finally {
      setDropdownOptionsLoading((prev) => ({ ...prev, [compositeKey]: false }))
    }
  }, [])

  const createDropdownOption = useCallback(async (
    metadataId: number,
    data: CreateDropdownOptionPayload
  ): Promise<DropdownOption> => {
    const res = await api.post<{ option: DropdownOption }>(
      `/admin/conversation-management/additional-properties/${metadataId}/dropdown-options`,
      data
    )
    setDropdownOptions((prev) => ({
      ...prev,
      [metadataId]: [...(prev[metadataId] ?? []), res.option]
    }))
    return res.option
  }, [])

  const updateDropdownOption = useCallback(async (
    optionId: number,
    data: UpdateDropdownOptionPayload
  ): Promise<DropdownOption> => {
    const res = await api.patch<{ option: DropdownOption }>(
      `/admin/conversation-management/additional-properties/dropdown-options/${optionId}`,
      data
    )
    // Update the option in the state
    setDropdownOptions((prev) => {
      const updated = { ...prev }
      for (const metadataId in updated) {
        if (updated[metadataId]) {
          updated[metadataId] = updated[metadataId]!.map(opt =>
            opt.id === optionId ? res.option : opt
          )
        }
      }
      return updated
    })
    return res.option
  }, [])

  const deleteDropdownOption = useCallback(async (optionId: number): Promise<void> => {
    await api.delete(`/admin/conversation-management/additional-properties/dropdown-options/${optionId}`)
    // Remove the option from the state
    setDropdownOptions((prev) => {
      const updated = { ...prev }
      for (const metadataId in updated) {
        if (updated[metadataId]) {
          updated[metadataId] = updated[metadataId]!.filter(opt => opt.id !== optionId)
        }
      }
      return updated
    })
  }, [])

  const fetchTenantTables = useCallback(async (): Promise<string[]> => {
    try {
      const res = await api.get<{ tables: string[] }>(
        '/admin/conversation-management/additional-properties/tenant-tables'
      )
      return res.tables ?? []
    } catch (err) {
      console.error('Failed to fetch tenant tables:', err)
      return []
    }
  }, [])

  // Dependencies CRUD
  const fetchDependencies = useCallback(async (metadataId: number): Promise<void> => {
    setDependenciesLoading((prev) => ({ ...prev, [metadataId]: true }))
    try {
      const res = await api.get<{ dependencies: PropertyDependency[] }>(
        `/admin/conversation-management/additional-properties/${metadataId}/dependencies`
      )
      setDependencies((prev) => ({ ...prev, [metadataId]: res.dependencies ?? [] }))
    } catch (err) {
      console.error('Failed to fetch dependencies:', err)
    } finally {
      setDependenciesLoading((prev) => ({ ...prev, [metadataId]: false }))
    }
  }, [])

  const createDependency = useCallback(async (
    metadataId: number,
    data: CreateDependencyPayload
  ): Promise<PropertyDependency> => {
    const res = await api.post<{ dependency: PropertyDependency }>(
      `/admin/conversation-management/additional-properties/${metadataId}/dependencies`,
      data
    )
    setDependencies((prev) => ({
      ...prev,
      [metadataId]: [...(prev[metadataId] ?? []), res.dependency]
    }))
    return res.dependency
  }, [])

  const updateDependency = useCallback(async (
    dependencyId: number,
    data: UpdateDependencyPayload
  ): Promise<PropertyDependency> => {
    const res = await api.patch<{ dependency: PropertyDependency }>(
      `/admin/conversation-management/additional-properties/dependencies/${dependencyId}`,
      data
    )
    // Update the dependency in the state
    setDependencies((prev) => {
      const updated = { ...prev }
      for (const metadataId in updated) {
        if (updated[metadataId]) {
          updated[metadataId] = updated[metadataId]!.map(dep =>
            dep.id === dependencyId ? res.dependency : dep
          )
        }
      }
      return updated
    })
    return res.dependency
  }, [])

  const deleteDependency = useCallback(async (dependencyId: number): Promise<void> => {
    await api.delete(`/admin/conversation-management/additional-properties/dependencies/${dependencyId}`)
    // Remove the dependency from the state
    setDependencies((prev) => {
      const updated = { ...prev }
      for (const metadataId in updated) {
        if (updated[metadataId]) {
          updated[metadataId] = updated[metadataId]!.filter(dep => dep.id !== dependencyId)
        }
      }
      return updated
    })
  }, [])

  const fetchAllDependenciesWithSummary = useCallback(async (search?: string, page: number = 1, limit: number = 10): Promise<{
    dependencies: PropertyDependency[]
    summary: {
      total: number
      propertiesWithDependencies: number
      showHideActions: number
      enableDisableActions: number
    }
    pagination: {
      total: number
      totalPages: number
      currentPage: number
      limit: number
    }
  }> => {
    const params = new URLSearchParams()
    if (search && search.trim()) {
      params.append('search', search.trim())
    }
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    const queryString = params.toString()
    const url = `/admin/conversation-management/additional-properties/dependencies/all${queryString ? `?${queryString}` : ''}`
    
    const res = await api.get<{
      dependencies: PropertyDependency[]
      summary: {
        total: number
        propertiesWithDependencies: number
        showHideActions: number
        enableDisableActions: number
      }
      pagination: {
        total: number
        totalPages: number
        currentPage: number
        limit: number
      }
    }>(url)
    return res
  }, [])

  const fetchDependencyById = useCallback(async (dependencyId: number): Promise<PropertyDependency | null> => {
    try {
      const res = await api.get<PropertyDependency>(
        `/admin/conversation-management/additional-properties/dependencies/${dependencyId}`
      )
      return res
    } catch (err) {
      console.error('Failed to fetch dependency by ID:', err)
      return null
    }
  }, [])

  const fetchPropertiesForDropdown = useCallback(async (): Promise<Array<{ id: number; displayName: string; columnName: string }>> => {
    try {
      const res = await api.get<Array<{ id: number; displayName: string; columnName: string }>>(
        `/admin/conversation-management/additional-properties/metadata/dropdown`
      )
      return res
    } catch (err) {
      console.error('Failed to fetch properties for dropdown:', err)
      return []
    }
  }, [])

  const filteredMetadata = search
    ? metadata.filter(
        (m) =>
          m.columnName.toLowerCase().includes(search.toLowerCase()) ||
          m.displayName.toLowerCase().includes(search.toLowerCase()),
      )
    : metadata

  return {
    items: filteredMetadata,
    allMetadata: metadata,
    loading,
    error,
    refetch,
    createProperty,
    renameProperty,
    deleteProperty,
    search,
    setSearch,
    total: pagination.total,
    summary,
    pagination,
    currentPage,
    setCurrentPage,
    dropdownOptions,
    dropdownOptionsLoading,
    fetchDropdownOptions,
    fetchDropdownOptionsByParent,
    createDropdownOption,
    updateDropdownOption,
    deleteDropdownOption,
    fetchTenantTables,
    dependencies,
    dependenciesLoading,
    fetchDependencies,
    fetchAllDependenciesWithSummary,
    fetchDependencyById,
    fetchPropertiesForDropdown,
    createDependency,
    updateDependency,
    deleteDependency,
  }
}

// Legacy alias — keeps the workflow-dropdowns hook and any other consumers from
// breaking while we transition to the new naming.
export const useConversationAdditionalPropertiesLegacy = useConversationAdditionalProperties

// ---------------------------------------------------------------------------
// Dependencies Management Hook (without metadata fetch)
// ---------------------------------------------------------------------------
export function useDependenciesManagement() {
  const fetchAllDependenciesWithSummary = useCallback(async (search?: string, page: number = 1, limit: number = 10): Promise<{
    dependencies: PropertyDependency[]
    summary: {
      total: number
      propertiesWithDependencies: number
      showHideActions: number
      enableDisableActions: number
    }
    pagination: {
      total: number
      totalPages: number
      currentPage: number
      limit: number
    }
  }> => {
    const params = new URLSearchParams()
    if (search && search.trim()) {
      params.append('search', search.trim())
    }
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    const queryString = params.toString()
    const url = `/admin/conversation-management/additional-properties/dependencies/all${queryString ? `?${queryString}` : ''}`
    
    const res = await api.get<{
      dependencies: PropertyDependency[]
      summary: {
        total: number
        propertiesWithDependencies: number
        showHideActions: number
        enableDisableActions: number
      }
      pagination: {
        total: number
        totalPages: number
        currentPage: number
        limit: number
      }
    }>(url)
    return res
  }, [])

  const fetchDependencyById = useCallback(async (dependencyId: number): Promise<PropertyDependency | null> => {
    try {
      const res = await api.get<PropertyDependency>(
        `/admin/conversation-management/additional-properties/dependencies/${dependencyId}`
      )
      return res
    } catch (err) {
      console.error('Failed to fetch dependency by ID:', err)
      return null
    }
  }, [])

  const fetchPropertiesForDropdown = useCallback(async (): Promise<Array<{ id: number; displayName: string; columnName: string }>> => {
    try {
      const res = await api.get<Array<{ id: number; displayName: string; columnName: string }>>(
        `/admin/conversation-management/additional-properties/metadata/dropdown`
      )
      return res
    } catch (err) {
      console.error('Failed to fetch properties for dropdown:', err)
      return []
    }
  }, [])

  const createDependency = useCallback(async (metadataId: number, data: {
    dependsOnPropertyId: number
    operator: string
    expectedValue?: string | null
    action: string
    condition?: string
    cssClass?: string | null
  }): Promise<PropertyDependency> => {
    const res = await api.post<{ dependency: PropertyDependency }>(
      `/admin/conversation-management/additional-properties/${metadataId}/dependencies`,
      data
    )
    return res.dependency
  }, [])

  const updateDependency = useCallback(async (dependencyId: number, data: {
    dependsOnPropertyId: number
    operator: string
    expectedValue?: string | null
    action: string
    condition?: string
    cssClass?: string | null
  }): Promise<PropertyDependency> => {
    const res = await api.put<{ dependency: PropertyDependency }>(
      `/admin/conversation-management/additional-properties/dependencies/${dependencyId}`,
      data
    )
    return res.dependency
  }, [])

  const deleteDependency = useCallback(async (dependencyId: number): Promise<void> => {
    await api.delete(`/admin/conversation-management/additional-properties/dependencies/${dependencyId}`)
  }, [])

  return {
    fetchAllDependenciesWithSummary,
    fetchDependencyById,
    fetchPropertiesForDropdown,
    createDependency,
    updateDependency,
    deleteDependency,
  }
}


// ---------------------------------------------------------------------------
// Root Cause Analysis
// ---------------------------------------------------------------------------

export interface ConversationRootCauseAnalysis {
  id: string
  categoryId: string
  subcategoryId: string | null
  categoryName?: string
  subcategoryName?: string
  probableCauses: string
  failureDefectMode: string
  status: boolean
  createdAt: string
  updatedAt: string
}

export function useConversationRootCauseAnalysis() {
  const [items, setItems] = useState<ConversationRootCauseAnalysis[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...(search && { search }),
    })

    api.get<{ items: ConversationRootCauseAnalysis[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/conversation-management/root-cause-analysis?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.items ?? [])
          setTotal(res.total ?? 0)
          setTotalPages(res.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch root cause analysis entries')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback(() => {
    setFetchKey((prev) => prev + 1)
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationRootCauseAnalysis> => {
      const created = await api.post<ConversationRootCauseAnalysis>('/admin/conversation-management/root-cause-analysis', body)
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationRootCauseAnalysis> => {
      const updated = await api.patch<ConversationRootCauseAnalysis>(`/admin/conversation-management/root-cause-analysis/${id}`, body)
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/root-cause-analysis/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationRootCauseAnalysis> => {
      return api.get<ConversationRootCauseAnalysis>(`/admin/conversation-management/root-cause-analysis/${id}`)
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}

// ---------------------------------------------------------------------------
// Conversation Forms
// ---------------------------------------------------------------------------

export interface ConversationForm {
  id: string
  name: string
  description: string | null
  status: boolean
  isDefault: boolean
  availableFor: number
  selectedProperties: string[]
  interactionProperties: string[]
  showSubject: boolean
  showDescription: boolean
  showAttachments: boolean
  useCustomSubject: boolean
  customSubjectLabel: string | null
  customSubjectValue: string | null
  useCustomDescription: boolean
  customDescriptionLabel: string | null
  customDescriptionValue: string | null
  formPropertiesOrder: string[]
  formSeparators: string
  propertyDefaults: Record<string, any>
  createdAt: string
  updatedAt: string
}

export function useConversationForms() {
  const [items, setItems] = useState<ConversationForm[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    params.append('page', page.toString())
    params.append('limit', limit.toString())

    api.get<{ forms: ConversationForm[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `/admin/conversation-management/forms?${params.toString()}`
    )
      .then((res) => {
        if (!cancelled) {
          setItems(res.forms ?? [])
          setTotal(res.pagination?.total ?? 0)
          setTotalPages(res.pagination?.totalPages ?? 0)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching forms')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey, page, limit, search])

  const refetch = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setFetchKey((k) => k + 1)
      resolve()
    })
  }, [])

  const create = useCallback(
    async (body: unknown): Promise<ConversationForm> => {
      const res = await api.post<{ form: ConversationForm }>('/admin/conversation-management/forms', body)
      const created = res.form
      setItems((prev) => [...prev, created])
      return created
    },
    [],
  )

  const update = useCallback(
    async (id: string, body: unknown): Promise<ConversationForm> => {
      const res = await api.patch<{ form: ConversationForm }>(`/admin/conversation-management/forms/${id}`, body)
      const updated = res.form
      setItems((prev) => prev.map((it) => it.id === id ? updated : it))
      return updated
    },
    [],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`/admin/conversation-management/forms/${id}`)
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const getById = useCallback(
    async (id: string): Promise<ConversationForm> => {
      const res = await api.get<{ form: ConversationForm }>(`/admin/conversation-management/forms/${id}`)
      return res.form
    },
    [],
  )

  return { items, loading, error, refetch, create, update, remove, getById, page, setPage, limit, setLimit, search, setSearch, total, totalPages }
}
